import type { RouteContext } from 'universal-router';

import {
  findSettingsPageByLegacyPath,
  findSettingsPageByPath,
} from 'src/features/settings/registry/settingsRegistry';
import { isRouteActionContext } from 'src/routes/types';
import { getClientSettingsTerm } from 'src/utils/clientSettingsPaths';

const isNewSettingsEnabled = (flags: unknown): boolean =>
  typeof flags === 'object' &&
  flags !== null &&
  'EnableNewSettings' in flags &&
  flags.EnableNewSettings === true;

/**
 * Sends old settings URLs to the page that replaced them, straight off each
 * registry entry's `legacyPaths`. It must stay the first child of /settings so
 * it runs before the old page's own route; with `new-settings` off the old
 * pages are still real, so it matches nothing.
 */
export const settingsLegacyPathRedirectRoute = {
  path: '/(.*)',
  action(context: RouteContext) {
    if (
      !isNewSettingsEnabled(context.flags) ||
      !isRouteActionContext(context)
    ) {
      return undefined;
    }

    const page = findSettingsPageByLegacyPath(
      context.pathname,
      getClientSettingsTerm(context.portalConfig.features.customLabels),
    );
    if (!page) return undefined;

    return {
      redirect: `${page.path}${context.search ?? ''}${context.hash ?? ''}`,
    };
  },
};

/**
 * Serves registry pages that no hand-written route claims, so one registry
 * entry is enough to make a page routable. It must stay the last child of
 * /settings; with `new-settings` off it matches nothing and the request falls
 * through to the not-found page as before.
 */
export const settingsRegistryRoute = {
  path: '/(.*)',
  async action(context: RouteContext) {
    if (
      !isNewSettingsEnabled(context.flags) ||
      !isRouteActionContext(context)
    ) {
      return undefined;
    }

    const clientTerm = getClientSettingsTerm(
      context.portalConfig.features.customLabels,
    );
    const page = findSettingsPageByPath(context.pathname, clientTerm);
    if (!page) return undefined;

    const { getRegistrySettingsPageResult } = await import(
      /* webpackChunkName: 'settings-registry-page' */ './registry-page'
    );
    return getRegistrySettingsPageResult(page);
  },
};
